import { useCallback, useEffect, useMemo, useState } from 'react';
import type { IChartEventSetting } from '@/pages/dashboard/filterPageComponents/overview/KpiEventFilter';
import type { EventDataItem, EventDataMap, IKpiChartEventSetting } from './types';
import { getOnlyOneKey, inferXAxisMatchTimeFormat } from './kpiEventUiUtils';
import { useDebouncedValue } from './useDebouncedValue';

export type { IKpiChartEventSetting } from './types';

const cloneEventMap = (m: EventDataMap | undefined): EventDataMap => {
  if (!m) {
    return {};
  }
  const o: EventDataMap = {};
  Object.keys(m).forEach(k => {
    const arr = m[k];
    o[k] = Array.isArray(arr) ? [...arr] : [];
  });
  return o;
};

/** 用日期键 + 每日条数序列化，避免父级 new 引用导致反复重置 */
const buildEventMapSyncKey = (m: EventDataMap | undefined): string => {
  if (!m) {
    return '';
  }
  return JSON.stringify(
    Object.keys(m)
      .sort()
      .map(k => [k, Array.isArray(m[k]) ? m[k].length : 0]),
  );
};

/** KPI 总览筛选条注入的同一套事件设置（与 KpiEventFilter 共享引用） */
export interface IKpiEventSettingFromFilter {
  chartEventSetting: IChartEventSetting;
  changeEventSetting: (data: IChartEventSetting) => void;
}

interface IUseEventMetricTrendKpiEventUiParams {
  enabled: boolean;
  processedEventData: EventDataMap | undefined;
  xAxisData: string[];
  /** 有值时：轴/气泡显隐与筛选条一致；气泡数据仍以图表内 processed 同步为准（enabled 时） */
  kpiEventFromFilter?: IKpiEventSettingFromFilter;
}

interface IUseEventMetricTrendKpiEventUiResult {
  chartEventSetting: IKpiChartEventSetting;
  changeEventSetting: (data: IKpiChartEventSetting) => void;
  timeFormat: string;
  isAxisBubbleControlledByFilter: boolean;
}

/**
 * KPI 事件设置：X 轴数字行 / 气泡开关 / bubbleEvent（供 EventWindows 与 chartRenderEventList）。
 * 当传入 kpiEventFromFilter 时，与筛选条共用轴/气泡与 bubble；「显示 A/B 实验详情」与筛选条 OR 合并（任一为 true 则平铺）。
 */
export function useEventMetricTrendKpiEventUi({
  enabled,
  processedEventData,
  xAxisData,
  kpiEventFromFilter,
}: IUseEventMetricTrendKpiEventUiParams): IUseEventMetricTrendKpiEventUiResult {
  /** 总览有筛选注入时，轴/气泡以筛选为准（与单卡是否开启 kpiEventUi 无关） */
  const isAxisBubbleControlledByFilter = Boolean(kpiEventFromFilter);

  const [internalChartEventSetting, setInternalChartEventSetting] =
    useState<IKpiChartEventSetting>({
      bubbleEvent: {},
      showEventAxis: true,
      showEventBubbles: true,
      showExperimentEventDetailFlat: false,
    });

  /**
   * 筛选条「显示事件」勾选会同时驱动多张 KPI 卡 echarts setOption。
   * 防抖 80ms 合并短时间内的反复切换，让浏览器在勾选框响应后再做重活。
   * 仅对筛选条来源的 axis/bubbles 防抖；弹窗写入 internal 路径保持立即生效。
   */
  const filterShowEventAxisRaw =
    kpiEventFromFilter?.chartEventSetting.showEventAxis ?? false;
  const filterShowEventBubblesRaw =
    kpiEventFromFilter?.chartEventSetting.showEventBubbles ?? false;
  const filterShowEventAxisDebounced = useDebouncedValue(
    filterShowEventAxisRaw,
    80,
  );
  const filterShowEventBubblesDebounced = useDebouncedValue(
    filterShowEventBubblesRaw,
    80,
  );

  /**
   * 筛选条事件设置弹窗确认时写入 filter 层的 bubbleEvent（按 X 轴时间分组）。
   * 提取其中所有事件的唯一 key（与弹窗去重逻辑同源），作为「当前用户选中的事件集合」；
   * 用此集合再去过滤本卡的 processedEventData，得到最终图表/X 轴使用的 bubbleEvent。
   *
   * 这样能同时覆盖两种 filter 层 bubbleEvent 形态：
   *   1) 用户未确认过时：filter 层 useKpiEventSetting 把 bubbleEvent 自动同步为
   *      `Record<event_type, events>`（全量），提取出的 key 集合即全部事件 → 不裁剪；
   *   2) 用户确认后：filter 层 bubbleEvent 为 `Record<time, events>` 子集，
   *      key 集合即用户选中事件 → 仅保留被选中事件，从而真正影响图表展示。
   */
  const filterSelectedEventKeys = useMemo<Set<string> | null>(() => {
    if (!kpiEventFromFilter) {
      return null;
    }
    const filterBubble = kpiEventFromFilter.chartEventSetting.bubbleEvent;
    if (!filterBubble || typeof filterBubble !== 'object') {
      return null;
    }
    const allRows = Object.values(filterBubble).flat() as Array<
    Record<string, unknown>
    >;
    return new Set(allRows.map((row) => getOnlyOneKey(row)));
  }, [kpiEventFromFilter]);

  const filterFilteredBubbleEvent = useMemo<EventDataMap | null>(() => {
    if (
      !kpiEventFromFilter ||
      !filterSelectedEventKeys ||
      !processedEventData
    ) {
      return null;
    }
    /** filter 层 bubble 尚未同步出任何事件 key 时，不按空集裁掉本卡（等同未裁剪） */
    if (filterSelectedEventKeys.size === 0) {
      return null;
    }
    const result: EventDataMap = {};
    Object.keys(processedEventData).forEach((date) => {
      const events = (processedEventData[date] || []) as EventDataItem[];
      const filtered = events.filter((e) =>
        filterSelectedEventKeys.has(
          getOnlyOneKey(e as unknown as Record<string, unknown>),
        ),
      );
      if (filtered.length > 0) {
        result[date] = filtered;
      }
    });
    return result;
  }, [kpiEventFromFilter, filterSelectedEventKeys, processedEventData]);

  const chartEventSetting = useMemo((): IKpiChartEventSetting => {
    if (!kpiEventFromFilter) {
      return internalChartEventSetting;
    }
    if (!enabled) {
      const base = kpiEventFromFilter.chartEventSetting as IKpiChartEventSetting;
      return {
        ...base,
        showEventAxis: filterShowEventAxisDebounced,
        showEventBubbles: filterShowEventBubblesDebounced,
        bubbleEvent:
          filterFilteredBubbleEvent ??
          (processedEventData !== undefined
            ? cloneEventMap(processedEventData)
            : cloneEventMap((base.bubbleEvent || {}) as EventDataMap)),
      };
    }
    return {
      ...internalChartEventSetting,
      /**
       * filter 弹窗确认会立即影响图表：用 filter 的选中事件集合裁剪本卡 processedEventData。
       * filterFilteredBubbleEvent 为 null 时（filter 未注入/数据未就绪）回退到 internal
       * （即自动同步过来的全量），保持加载阶段与原行为一致、避免短暂空白。
       */
      bubbleEvent:
        filterFilteredBubbleEvent ?? internalChartEventSetting.bubbleEvent,
      showEventAxis: filterShowEventAxisDebounced,
      showEventBubbles: filterShowEventBubblesDebounced,
      /** 与筛选条弹窗同源；单卡弹窗写入 internal，二者任一为 true 即平铺实验 */
      showExperimentEventDetailFlat:
        kpiEventFromFilter.chartEventSetting.showExperimentEventDetailFlat ===
          true ||
        internalChartEventSetting.showExperimentEventDetailFlat === true,
    };
  }, [
    kpiEventFromFilter,
    enabled,
    internalChartEventSetting,
    filterShowEventAxisDebounced,
    filterShowEventBubblesDebounced,
    filterFilteredBubbleEvent,
    processedEventData,
  ]);

  const syncKey = useMemo(
    () => buildEventMapSyncKey(processedEventData),
    [processedEventData],
  );

  // 仅当「按日条数/日期键」真变时同步全量 eventData；勿把 processedEventData 放进依赖（滚动等导致新引用会冲掉弹窗筛选）
  useEffect(() => {
    if (!enabled || processedEventData === undefined) {
      return;
    }
    setInternalChartEventSetting(prev => ({
      ...prev,
      bubbleEvent: cloneEventMap(processedEventData),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 刻意只用 syncKey；processedEventData 引用不稳
  }, [enabled, syncKey]);

  const changeEventSetting = useCallback(
    (data: IKpiChartEventSetting) => {
      if (!kpiEventFromFilter) {
        setInternalChartEventSetting(prev => ({
          bubbleEvent:
            data.bubbleEvent === prev.bubbleEvent
              ? prev.bubbleEvent
              : cloneEventMap(data.bubbleEvent),
          showEventAxis: data.showEventAxis,
          showEventBubbles: data.showEventBubbles,
          showExperimentEventDetailFlat:
            data.showExperimentEventDetailFlat ?? false,
        }));
        return;
      }
      if (!enabled) {
        kpiEventFromFilter.changeEventSetting(
          data as unknown as IChartEventSetting,
        );
        return;
      }
      setInternalChartEventSetting(prev => ({
        ...prev,
        bubbleEvent:
          data.bubbleEvent === prev.bubbleEvent
            ? prev.bubbleEvent
            : cloneEventMap(data.bubbleEvent),
        showExperimentEventDetailFlat:
          data.showExperimentEventDetailFlat ??
          prev.showExperimentEventDetailFlat ??
          false,
      }));
    },
    [enabled, kpiEventFromFilter],
  );

  const timeFormat = useMemo(
    () => inferXAxisMatchTimeFormat(xAxisData),
    [xAxisData],
  );

  return {
    chartEventSetting,
    changeEventSetting,
    timeFormat,
    isAxisBubbleControlledByFilter,
  };
}
